import { useEffect, useRef, useState } from 'react';
import type { Product } from '@/api/models';
import { GarmentSide, GarmentVisual } from '@/components/garment/GarmentVisual';
import { ChevronDown } from '@/components/ui/Icons';
import { formatMoney } from '@/lib/money';
import { retrigger } from '@/lib/retrigger';

interface Props {
  products: Product[];
  onOpen: (index: number) => void;
  /** Category name for the idle line, e.g. "10 casual blazers on the rail". */
  label?: string;
  /** Swing every piece in when the rail first shows (used when switching category). */
  swingIn?: boolean;
}

const subtitle = (p: Product) => {
  const kind = p.category?.name.replace(/s$/, '') ?? 'Piece';
  return `${kind}${p.colorName ? ` in ${p.colorName.toLowerCase()}` : ''}, ${formatMoney(p.price, p.currency)}`;
};

/** The hanging rail. Hovering (or focusing) a piece swings it round to face the viewer. */
export function Rack({ products, onOpen, label, swingIn }: Props) {
  const [active, setActive] = useState<number | null>(null);
  const slots = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (swingIn) slots.current.forEach((s) => retrigger(s, 'swing'));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const activate = (i: number | null) => {
    if (i === active) return;
    setActive(i);
    if (i === null) return;
    retrigger(slots.current[i], 'swing');
    retrigger(slots.current[i - 1], 'nudgeL');
    retrigger(slots.current[i + 1], 'nudgeR');
  };

  return (
    <>
      <div className="rail" />
      <div className="rack" onMouseLeave={() => activate(null)}>
        {products.map((p, i) => (
          <button
            key={p.id}
            ref={(el) => { slots.current[i] = el; }}
            className={'slot' + (active === i ? ' active' : '')}
            aria-label={`${p.name}, ${formatMoney(p.price, p.currency)}`}
            onMouseEnter={() => activate(i)}
            onFocus={() => activate(i)}
            onClick={() => onOpen(i)}
          >
            <span className="g">
              <span className="side"><GarmentSide garment={p.garment} images={p.images} drawn={p.garmentSet} /></span>
              <span className="front"><GarmentVisual garment={p.garment} images={p.images} decorative sizes="176px" /></span>
            </span>
          </button>
        ))}
      </div>
      <RackInfo product={active !== null ? products[active] : null} count={products.length} label={label} />
      <div style={{ textAlign: 'center' }}>
        <button className="explore" onClick={() => onOpen(active ?? 0)}>Explore pieces <ChevronDown /></button>
      </div>
    </>
  );
}

/** Name and price under the rail, with the short fade between pieces. */
function RackInfo({ product, count, label }: { product: Product | null; count: number; label?: string }) {
  const what = label ? label.toLowerCase() : `piece${count === 1 ? '' : 's'}`;
  const next = product
    ? { n: product.name, s: subtitle(product) }
    : { n: `${count} ${what} on the rail`, s: 'Hover to turn a piece, click to look closer' };
  const [shown, setShown] = useState(next);
  const [swap, setSwap] = useState(false);

  useEffect(() => {
    setSwap(true);
    const t = window.setTimeout(() => { setShown(next); setSwap(false); }, 160);
    return () => window.clearTimeout(t);
  }, [next.n, next.s]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={'info' + (swap ? ' swap' : '')} aria-live="polite">
      <div className="n">{shown.n}</div>
      <div className="s">{shown.s}</div>
    </div>
  );
}

export function RackSkeleton() {
  return (
    <>
      <div className="rail" />
      <div className="rack" aria-hidden="true">
        {Array.from({ length: 10 }, (_, i) => (
          <span key={i} className="slot"><span className="skel" style={{ position: 'absolute', top: 18, left: 3, width: 30, height: 190, borderRadius: 14 }} /></span>
        ))}
      </div>
      <div className="info"><div className="n">Loading the rail</div><div className="s">One moment</div></div>
    </>
  );
}
